import React, { useState, useEffect } from "react";
import axios from "axios";
import { createMRTColumnHelper } from "material-react-table";
import Chip from "@mui/material/Chip";
import { Box, Tooltip, IconButton } from "@mui/material";
import { Icon } from "@iconify/react";
import Swal from "sweetalert2";
import { useSelector } from "react-redux";
import { baseUrl } from "../../../helper/urls";
import AdminCustomTable from "../../../components/custom_table/AdminCustomTable";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const CreateFaqs = () => {
  const { token } = useSelector((state) => state.auth);

  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [userType, setUserType] = useState("");
  const [faqsData, setFaqsData] = useState([]);
  const [loading, setLoading] = useState(true);

  const [editMode, setEditMode] = useState(false);
  const [editFaqId, setEditFaqId] = useState(null);

  const allowedFields = ["student", "counselor", "both"];

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!question.trim() || !answer.trim() || !userType.trim()) {
      toast.error("All fields are required.");
      return;
    }

    if (!allowedFields.includes(userType)) {
      toast.error("Invalid user type selected.");
      return;
    }

    try {
      const url = editMode
        ? `${baseUrl}/api/faq/update/${editFaqId}`
        : `${baseUrl}/api/faq/create`;

      const response = await axios.post(
        url,
        { question, answer, user_type: userType },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200 || response.status === 201) {
        toast.success(
          editMode ? "FAQ updated successfully!" : "FAQ created successfully!"
        );
        resetForm();
        fetchFaqsList();
      }
    } catch (error) {
      console.error(error);
      toast.error(editMode ? "Failed to update FAQ." : "Failed to create FAQ.");
    }
  };

  const resetForm = () => {
    setQuestion("");
    setAnswer("");
    setUserType("");
    setEditMode(false);
    setEditFaqId(null);
  };

  const fetchFaqsList = async () => {
    try {
      const response = await axios.get(`${baseUrl}/api/faq/admin`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      setFaqsData(response.data.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching FAQs:", error);
      setLoading(false);
    }
  };

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      const response = await axios.post(
        `${baseUrl}/api/faq/toggle/${id}`,
        { status: currentStatus },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        toast.success("FAQ status updated!");
        fetchFaqsList();
      }
    } catch (error) {
      console.error("Error toggling status:", error);
      toast.error("Failed to update status.");
    }
  };

  const handleDeleteFaq = async (id) => {
    const result = await Swal.fire({
      html: `<div style="font-size: 30px; font-weight: bold;">Are you sure?</div>`,
      text: "Do you really want to delete this FAQ?",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.get(`${baseUrl}/api/faq/destroy/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.status === 200) {
          toast.success("FAQ deleted successfully!");
          fetchFaqsList();
        }
      } catch (error) {
        console.error("Error deleting FAQ:", error);
        toast.error("Failed to delete FAQ.");
      }
    }
  };

  const handleEditFaq = (faq) => {
    setEditFaqId(faq.id);
    setQuestion(faq.question);
    setAnswer(faq.answer);
    setUserType(faq.user_type);
    setEditMode(true);
  };

  useEffect(() => {
    fetchFaqsList();
  }, []);

  const columnHelper = createMRTColumnHelper();
  const columns = [
    columnHelper.accessor("question", {
      header: "Question",
      Cell: (cell) => cell.row.original?.question || "-",
    }),
    columnHelper.accessor("answer", {
      header: "Answer",
      Cell: (cell) => {
        const answer = cell.row.original?.answer || "-";
        const words = answer.split(" ");
        const shortAnswer =
          words.length > 10 ? words.slice(0, 10).join(" ") + "..." : answer;
        return shortAnswer;
      },
    }),
    columnHelper.accessor("user_type", {
      header: "User Type",
      Cell: ({ cell }) => {
        const userType = cell.row.original?.user_type;
        const colorMap = {
          student: "primary",
          counselor: "secondary",
          both: "success",
        };

        return (
          <Chip
            label={
              userType
                ? userType.charAt(0).toUpperCase() + userType.slice(1)
                : "-"
            }
            color={colorMap[userType] || "default"}
            variant="outlined"
            size="small"
          />
        );
      },
    }),
    columnHelper.accessor("Actions", {
      header: "Actions",
      Cell: ({ cell }) => {
        const row = cell.row.original;
        const isActive = row.status === 1;

        return (
          <Box display="flex" alignItems="center" gap={1}>
            <Tooltip title={isActive ? "Hide FAQ" : "Show FAQ"}>
              <IconButton
                onClick={() => handleToggleStatus(row.id, row.status)}
              >
                <Icon
                  icon={!isActive ? "mdi:eye-off-outline" : "mdi:eye-outline"}
                  color={!isActive ? "red" : "green"}
                  width="22"
                  height="22"
                />
              </IconButton>
            </Tooltip>

            <Tooltip title="Edit FAQ" arrow>
              <button
                onClick={() => handleEditFaq(row)}
                className="w-32-px h-32-px me-8 bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
              >
                <Icon icon="lucide:edit" />
              </button>
            </Tooltip>
            <Tooltip title="Delete FAQ" arrow>
              <button
                onClick={() => handleDeleteFaq(row.id)}
                className="w-32-px h-32-px me-8 bg-danger-focus text-danger-main rounded-circle border-0 d-inline-flex align-items-center justify-content-center"
                style={{ cursor: "pointer" }}
              >
                <Icon icon="mingcute:delete-2-line" />
              </button>
            </Tooltip>
          </Box>
        );
      },
    }),
  ];

  return (
    <div className="mt-2">
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="card shadow-sm p-20 mb-20">
        <h5 className="mb-4" style={{ color: "#19477C" }}>
          {editMode ? "Edit FAQ" : "Create New FAQ"}
        </h5>
        <form onSubmit={handleSubmit} className="row g-3">
          <div className="col-md-6">
            <label className="form-label fw-bold">Question</label>
            <input
              type="text"
              className="form-control"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="Enter your question"
            />
          </div>

          <div className="col-md-6">
            <label className="form-label fw-bold">User Type</label>
            <select
              className="form-select"
              value={userType}
              onChange={(e) => setUserType(e.target.value)}
            >
              <option value="">-- Select --</option>
              <option value="student">Student</option>
              <option value="counselor">Counselor</option>
              <option value="both">Both</option>
            </select>
          </div>

          <div className="col-md-12">
            <label className="form-label fw-bold">Answer</label>
            <textarea
              className="form-control"
              rows="4"
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              placeholder="Enter the answer"
            />
          </div>

          <div className="row justify-content-end">
            <div className="col-3 mt-16">
              <button
                type="submit"
                className="btn btn-primary-600 radius-8 w-100"
              >
                {editMode ? "Update FAQ" : "Create FAQ"}
              </button>
            </div>
            {editMode && (
              <div className="col-3 mt-16">
                <button
                  type="button"
                  className="btn btn-secondary radius-8 w-100"
                  onClick={resetForm}
                >
                  Cancel Edit
                </button>
              </div>
            )}
          </div>
        </form>
      </div>

      <div className="mt-10">
        <AdminCustomTable columns={columns} data={faqsData} loading={loading} />
      </div>
    </div>
  );
};

export default CreateFaqs;
